import { assert, assertNever, lruCached } from '~/util';
import { defaultSampleAt } from '../waveform';
import { constraintCache } from './constraints';
import type { WaveformApi } from '../waveform';
import type { ComputedConstraints } from './constraints';
import type { KaiserWaveform } from '~/models/studio';
import type { Bounds, Vec2 } from '~/util';

export const wfKaiser: WaveformApi<KaiserWaveform> = {
  samples: kaiserSamples,
  sampleAt: defaultSampleAt,
  bounds: kaiserBounds,
  points: kaiserPoints,
  duration: kaiserDuration,
  area: kaiserArea
};

function kaiserDuration(wf: KaiserWaveform): number {
  if (wf.constraint.kind === 'duration') {
    return getKaiserExactConstraints(wf).duration;
  } else {
    return wf.constraint.duration;
  }
}

function kaiserArea(wf: KaiserWaveform): number {
  if (wf.constraint.kind === 'area') {
    return getKaiserExactConstraints(wf).area;
  } else {
    return wf.constraint.area;
  }
}

function kaiserNormSamples(beta: number, duration: number): number[] {
  const kaiserSamples: number[] = [];
  const M = duration;
  if (M == 1) {
    return [1];
  }
  const alpha = (M - 1) / 2;
  const invAlpha = 1 / alpha;
  const invI0Beta = 1 / i0(beta);
  for (let n = 0; n < M; n++) {
    const alphaPart = (n - alpha) * invAlpha;
    kaiserSamples.push(
      i0(beta * Math.sqrt(1 - alphaPart * alphaPart)) * invI0Beta
    );
  }
  return kaiserSamples;
}

const kaiserNormSamplesCached = lruCached(
  kaiserNormSamples,
  100,
  (beta, duration) => `${beta}_${duration}`
);

function kaiserSum(beta: number, duration: number) {
  assert(Number.isInteger(duration));
  const samples = kaiserNormSamplesCached(beta, duration);
  const sum = samples.reduce((acc, val) => acc + val, 0);
  return sum;
}

const kaiserSumCached = lruCached(
  kaiserSum,
  2000,
  (beta, duration) => `${beta}_${duration}`
);

function getKaiserExactConstraints(wf: KaiserWaveform): ComputedConstraints {
  const fromCache = constraintCache.get(wf);
  if (fromCache != null) {
    return fromCache;
  }
  const computed = computeKaiserExactConstraints(wf);
  constraintCache.set(wf, computed);
  return computed;
}

function computeKaiserExactConstraints(
  wf: KaiserWaveform
): ComputedConstraints {
  switch (wf.constraint.kind) {
    case 'maxVal': {
      const { duration, area } = wf.constraint;
      if (duration <= 0) {
        return {
          duration: 0,
          maxVal: 0,
          area: 0
        };
      }
      const sum = kaiserSumCached(wf.beta, duration);
      const maxVal = (wf.constraint.area / sum) * 1000;
      return { duration, maxVal, area };
    }
    case 'area': {
      const { duration, maxVal } = wf.constraint;
      if (duration <= 0) {
        return {
          duration: 0,
          maxVal: 0,
          area: 0
        };
      }
      const sum = kaiserSumCached(wf.beta, duration);
      const area = (sum * maxVal) / 1000;
      return { duration, maxVal, area };
    }
    case 'duration': {
      const { maxVal, area } = wf.constraint;
      if (maxVal <= 0 || area <= 0) {
        return {
          duration: 0,
          maxVal: 0,
          area: 0
        };
      }
      let start = 0;
      //NOTE: 10000 is the max duration in the app
      let end = 10000;
      let duration = 0;
      const targetSum = (area / maxVal) * 1000;

      while (start < end) {
        duration = Math.floor((start + end) / 2);
        const currentSum = kaiserSumCached(wf.beta, duration);
        if (currentSum < targetSum) {
          start = duration + 1;
        } else if (currentSum > targetSum) {
          end = duration;
        } else {
          break;
        }
      }
      return {
        duration,
        maxVal,
        area
      };
    }
    default:
      return assertNever(wf.constraint);
  }
}

function kaiserSamples(wf: KaiserWaveform): number[] {
  const { duration, maxVal } = getKaiserExactConstraints(wf);
  const norm = kaiserNormSamplesCached(wf.beta, duration);
  return norm.map((n) => n * maxVal);
}

function kaiserBounds(wf: KaiserWaveform): Bounds {
  switch (wf.constraint.kind) {
    case 'maxVal':
      return {
        min: 0,
        max:
          (wf.constraint.area /
            kaiserSumCached(wf.beta, wf.constraint.duration)) *
          1000
      };
    case 'duration':
    case 'area':
      return { min: 0, max: wf.constraint.maxVal };
    default:
      assertNever(wf.constraint);
  }
}

function kaiserPoints(wf: KaiserWaveform, xOffset: number): Vec2[] {
  const samples = kaiserSamples(wf);
  return samples.map((sample, i) => ({ x: xOffset + i, y: sample }));
}

/** Chebyshev polynomials */
// prettier-ignore
const A = [
  -4.415341646479339e-18,  3.330794518822238e-17, -2.431279846547955e-16,
   1.715391285555133e-15, -1.168533287799345e-14,  7.676185498604936e-14,
  -4.856446783111929e-13,  2.955052663129640e-12, -1.726826291441556e-11,
   9.675809035373237e-11, -5.189795601635263e-10,  2.659823724682387e-9,
  -1.300025009986248e-8,   6.046995022541919e-8,  -2.670793853940612e-7,
   1.117387539120104e-6,  -4.416738358458750e-6,   1.644844807072890e-5,
  -5.754195010082104e-5,   1.885028850958417e-4,  -5.763755745385824e-4,
   1.639475616941336e-3,  -4.324309995050576e-3,    1.05464603945950e-2,
  -2.373741480589947e-2,   4.930528423967071e-2,  -9.490109704804764e-2,
   1.716209015222088e-1,  -3.046826723431984e-1,   6.767952744094761e-1,
];

// prettier-ignore
const B = [
  -7.23318048787475e-18, -4.830504485944182e-18,  4.465621420296760e-17,
   3.46122286769746e-17, -2.827623980516583e-16, -3.425485619677219e-16,
   1.77256013305652e-15,  3.811680669352622e-15, -9.554846698828307e-15,
  -4.15056934728722e-14,  1.540086217521410e-14,  3.852778382742143e-13,
   7.18012445138367e-13, -1.794178531506806e-12, -1.321581184044771e-11,
  -3.14991652796324e-11,  1.188914710784644e-11,  4.940602388224970e-10,
   3.39623202570839e-9,   2.266668990498178e-8,   2.048918589469064e-7,
   2.89137052083476e-6,   6.889758346916823e-5,   3.369116478255694e-3,
   8.04490411014109e-1,
];

function i0(x: number) {
  if (x < 0) {
    x = -x;
  }
  if (x <= 8.0) {
    return Math.exp(x) * chbevl(x / 2 - 2, A);
  }

  return (Math.exp(x) * chbevl(32 / x - 2, B)) / Math.sqrt(x);
}

function chbevl(x: number, vals: number[]) {
  let b0 = vals[0];
  let b1 = 0.0;
  let b2 = 0.0;

  for (let i = 1; i < vals.length; i++) {
    b2 = b1;
    b1 = b0;
    b0 = x * b1 - b2 + vals[i];
  }

  return 0.5 * (b0 - b2);
}
